/**
 * OWNERSHIP: console-only
 *
 * This empty state component is console-specific because:
 * - Uses console's Button component with variant props
 * - Designed for authenticated user flows with action buttons
 * - Uses lucide-react icons which are installed in console
 *
 * Migration candidate: Could be moved to @knitknot/shared if hosted app
 * needs empty states for error pages. Would need to remove Button dependency
 * and use plain HTML buttons or make Button shared.
 */
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from './button';
import { interceptPlainClick } from './intercept-plain-click';
import { cn } from '../../utils';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline';
  /** Disables the button — e.g. while its mutation is in flight. */
  disabled?: boolean;
  /**
   * Destination for an action that NAVIGATES ("Back to Library", "Go to Home",
   * "Connect in Settings"). The button renders as a real `<a>` so ⌘/Ctrl-click,
   * middle-click and right-click "Open in new tab" all work — same contract as
   * `StatCell`'s `href` and `DataTable`'s `rowHref`. `onClick` still owns the
   * plain left-click, which is what keeps client-side routing. Omit it for an
   * action that MUTATES (run a benchmark, clear filters) — those are buttons.
   */
  href?: string;
}

/**
 * Which of the differentiated non-data situations this render represents.
 * Situation 3 ("request failed") is NOT here — that's {@link ErrorState}, a
 * sibling `EmptyState` wrapper with its own fixed icon + wired Retry (read it
 * for the precedent this prop follows). Omit `situation` entirely for a plain
 * informational empty state with no CTA semantics — existing call sites keep
 * their current look bit-for-bit.
 *
 * - `'cold-start'` — never had data. The obvious primary action (run a
 *   benchmark, go set something up) renders as a real `Button` — this is the
 *   "prose where a button belongs" failure mode the differentiated system
 *   exists to kill. Prominence comes from PLACEMENT, not fill color: the
 *   button chrome is the one canonical look (see `button.tsx` — `default`
 *   and `outline` render identically, a teal-filled primary is banned in
 *   product UI), so `situation` doesn't change the button's variant.
 * - `'filtered'` — the active search/filters matched zero rows; there IS
 *   data, the view just over-narrowed it. Pass `onClearFilters` for the
 *   canonical "Clear filters" action instead of building your own `action`
 *   object — every filtered-empty state then behaves identically.
 * - `'not-applicable'` — informational only, e.g. "appears after your first
 *   benchmark." NO action is ever rendered for this situation, even if the
 *   caller passes one — nothing the user does changes it, so offering a
 *   button would be a broken promise (hard rule, dev-mode warns on misuse).
 */
export type EmptyStateSituation = 'cold-start' | 'filtered' | 'not-applicable';

export interface EmptyStateProps {
  /**
   * Icon component to display (from lucide-react)
   */
  icon: LucideIcon;

  /**
   * Primary message (e.g., "No items found")
   */
  title: string;

  /**
   * Secondary descriptive message or call to action
   */
  description?: string;

  /**
   * The differentiated situation this render encodes — see
   * {@link EmptyStateSituation}. Optional for back-compat; every existing
   * call site that omits it renders exactly as before.
   */
  situation?: EmptyStateSituation;

  /**
   * Optional action button configuration. Ignored entirely when
   * `situation === 'not-applicable'` (hard rule — never offer an action the
   * user can't take).
   */
  action?: EmptyStateAction;

  /**
   * Optional secondary action button. Also suppressed for `'not-applicable'`.
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };

  /**
   * `situation="filtered"` convenience: renders the canonical "Clear filters"
   * button as `action` when no explicit `action` prop is passed. Wire this to
   * whatever resets the page's search + filter state (the same state that
   * feeds the removable filter pills) so every filtered-empty state clears
   * the same way. Ignored for other situations.
   */
  onClearFilters?: () => void;

  /**
   * Custom icon size (defaults to h-12 w-12)
   */
  iconSize?: string;

  /**
   * Custom padding (defaults to p-12)
   */
  padding?: string;

  /**
   * Additional className for container
   */
  className?: string;

  /**
   * Custom icon color (defaults to text-ink-supporting)
   */
  iconColor?: string;

  /**
   * Whether to show in compact mode (smaller padding and icon)
   */
  compact?: boolean;
}

/**
 * EmptyState Component
 *
 * A standardized empty state component used throughout the application
 * to display when no content is available. Extended (2026-07-25) with the
 * `situation` prop so call sites declare WHICH of the four differentiated
 * non-data states they're in and this component owns the treatment (default
 * button emphasis, the "Clear filters" convenience, and the not-applicable
 * no-CTA guarantee) — see {@link EmptyStateSituation}. `ErrorState` (situation
 * 3, "request failed") stays a separate sibling wrapper, the same shape it
 * already was before this change.
 *
 * @example
 * ```tsx
 * import { FolderOpen } from 'lucide-react';
 *
 * // Cold start — the primary action a user can take right now.
 * <EmptyState
 *   icon={FolderOpen}
 *   situation="cold-start"
 *   title="No projects yet"
 *   description="Create your first project to get started"
 *   action={{ label: "Create Project", onClick: handleCreate }}
 * />
 *
 * // Filtered to zero — the active filters, not the data, are why it's empty.
 * <EmptyState
 *   icon={FolderOpen}
 *   situation="filtered"
 *   title="No projects match your filters"
 *   onClearFilters={() => { setSearch(''); setFilters([]); }}
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  situation,
  action: actionProp,
  secondaryAction: secondaryActionProp,
  onClearFilters,
  iconSize,
  padding,
  className,
  iconColor,
  compact = false,
}) => {
  const isNotApplicable = situation === 'not-applicable';

  if (process.env.NODE_ENV !== 'production' && isNotApplicable && (actionProp || onClearFilters)) {
    // eslint-disable-next-line no-console
    console.warn(
      'EmptyState: situation="not-applicable" never renders an action (hard rule — ' +
        'nothing the user does changes this state). The `action`/`onClearFilters` prop ' +
        'passed alongside it is being ignored.',
    );
  }

  // `filtered` convenience: the canonical "Clear filters" button, unless the
  // caller already built its own `action`.
  const resolvedAction: EmptyStateAction | undefined = isNotApplicable
    ? undefined
    : (actionProp ??
      (situation === 'filtered' && onClearFilters
        ? { label: 'Clear filters', onClick: onClearFilters, variant: 'outline' }
        : undefined));

  // ONE button chrome everywhere (button.tsx: 'default'/'outline' render
  // identically, a filled/teal primary is banned in product UI) — `situation`
  // changes WHETHER an action renders and what it defaults to (the "Clear
  // filters" convenience), never its color. 'outline' is the historical
  // default across every existing call site; kept as-is for back-compat.
  const defaultVariant = 'outline';

  const secondaryAction = isNotApplicable ? undefined : secondaryActionProp;

  // Default sizes based on compact mode
  const defaultIconSize = compact ? 'h-8 w-8' : 'h-12 w-12';
  const defaultPadding = compact ? 'p-8' : 'p-12';
  const defaultIconColor = iconColor || 'text-ink-supporting';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        padding || defaultPadding,
        className
      )}
    >
      {/* Icon - decorative, hidden from screen readers */}
      <Icon
        className={cn(
          iconSize || defaultIconSize,
          defaultIconColor,
          'mb-4'
        )}
        aria-hidden="true"
      />

      {/* Primary message */}
      <p
        className={cn(
          'font-medium text-ink-content',
          compact ? 'text-base mb-1' : 'text-lg mb-2'
        )}
      >
        {title}
      </p>

      {/* Secondary message */}
      {description && (
        <p
          className={cn(
            'text-ink-supporting',
            compact ? 'text-xs mb-3' : 'text-sm mb-4'
          )}
        >
          {description}
        </p>
      )}

      {/* Actions */}
      {(resolvedAction || secondaryAction) && (
        <div className="flex items-center gap-2 mt-2">
          {resolvedAction &&
            (resolvedAction.href && !resolvedAction.disabled ? (
              // A navigating action is a LINK wearing the button's chrome
              // (`asChild` + `<a>`): the browser owns every open-elsewhere
              // gesture and `onClick` intercepts only the plain left-click, so
              // client-side routing is unchanged. Router-agnostic on purpose —
              // hosted has no react-router.
              <Button
                asChild
                variant={resolvedAction.variant || defaultVariant}
                size={compact ? 'sm' : 'default'}
              >
                <a href={resolvedAction.href} onClick={(e) => interceptPlainClick(e, resolvedAction.onClick)}>
                  {resolvedAction.label}
                </a>
              </Button>
            ) : (
              <Button
                variant={resolvedAction.variant || defaultVariant}
                onClick={resolvedAction.onClick}
                disabled={resolvedAction.disabled}
                size={compact ? 'sm' : 'default'}
              >
                {resolvedAction.label}
              </Button>
            ))}
          {secondaryAction && (
            <Button
              variant="ghost"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
              size={compact ? 'sm' : 'default'}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
